import { createAppRequest } from '@/services/request';
import type { ApiRequestOptions } from './types/common';

const request = createAppRequest();

export interface WorkflowInstanceRecord {
  uuid: string;
  flowNo: string;
  flowName?: string;
  flowTemplateCode?: string;
  businessType: string;
  businessUuid: string;
  businessNo: string;
  status: string;
  priority: string;
  currentNodeKey?: string;
  currentNodeName?: string;
  dueAt?: string;
  isOverdue: boolean;
  summary?: string;
  cancelReason?: string;
  startedByMemberUuid?: string;
  startedAt: string;
  finishedAt?: string;
  createdAt: string;
}

export interface WorkflowTaskRecord {
  uuid: string;
  instanceUuid: string;
  taskName: string;
  taskType: string;
  nodeKey: string;
  assigneeMemberUuid?: string;
  status: string;
  priority: string;
  dueAt?: string;
  isOverdue: boolean;
  claimedAt?: string;
  transferredFromMemberUuid?: string;
  delegatedByMemberUuid?: string;
  approvedByMemberUuid?: string;
  action?: string;
  actionReason?: string;
  comment?: string;
  completedAt?: string;
  createdAt: string;
}

export interface WorkflowTaskLogRecord {
  uuid: string;
  instanceUuid: string;
  taskUuid?: string;
  businessType: string;
  businessNo: string;
  action: string;
  beforeStatus?: string;
  afterStatus?: string;
  operatorMemberUuid?: string;
  fromMemberUuid?: string;
  toMemberUuid?: string;
  comment?: string;
  operatedAt: string;
}

export interface WorkflowSummaryRecord {
  pendingTaskCount: number;
  approvedTaskCount: number;
  rejectedTaskCount: number;
  overdueTaskCount: number;
  runningInstanceCount: number;
}

export interface CompleteWorkflowTaskPayload {
  comment?: string;
}

export interface TransferWorkflowTaskPayload {
  toMemberUuid: string;
  comment?: string;
}

export async function getWorkflowSummary(options?: ApiRequestOptions) {
  return request<WorkflowSummaryRecord>('/bff/workflow/summary', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getWorkflowInstances(options?: ApiRequestOptions) {
  return request<WorkflowInstanceRecord[]>('/bff/workflow/instances', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getWorkflowInstance(instanceUuid: string, options?: ApiRequestOptions) {
  return request<WorkflowInstanceRecord>(`/bff/workflow/instances/${instanceUuid}`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getWorkflowTaskLogs(instanceUuid: string, options?: ApiRequestOptions) {
  return request<WorkflowTaskLogRecord[]>(`/bff/workflow/instances/${instanceUuid}/logs`, {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getWorkflowTasks(options?: ApiRequestOptions) {
  return request<WorkflowTaskRecord[]>('/bff/workflow/tasks', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function getMyWorkflowTasks(options?: ApiRequestOptions) {
  return request<WorkflowTaskRecord[]>('/bff/workflow/tasks/my', {
    method: 'GET',
    ...(options || {}),
  });
}

export async function approveWorkflowTask(
  taskUuid: string,
  data: CompleteWorkflowTaskPayload = {},
  options?: ApiRequestOptions,
) {
  return request(`/bff/workflow/tasks/${taskUuid}/approve`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function rejectWorkflowTask(
  taskUuid: string,
  data: CompleteWorkflowTaskPayload = {},
  options?: ApiRequestOptions,
) {
  return request(`/bff/workflow/tasks/${taskUuid}/reject`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function transferWorkflowTask(
  taskUuid: string,
  data: TransferWorkflowTaskPayload,
  options?: ApiRequestOptions,
) {
  return request(`/bff/workflow/tasks/${taskUuid}/transfer`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function terminateWorkflowInstance(
  instanceUuid: string,
  data: { reason?: string } = {},
  options?: ApiRequestOptions,
) {
  return request(`/bff/workflow/instances/${instanceUuid}/terminate`, {
    method: 'POST',
    data,
    ...(options || {}),
  });
}

export async function markWorkflowOverdueTasks(options?: ApiRequestOptions) {
  return request<{ markedCount: number }>('/bff/workflow/tasks/mark-overdue', {
    method: 'POST',
    data: {},
    ...(options || {}),
  });
}
